import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

// State values to track the model opening state
const { CLOSED, OPENING, OPEN, CLOSING } = {
  CLOSED: 'closed',
  OPENING: 'opening',
  OPEN: 'open',
  CLOSING: 'closing',
};

// Styles for the modal when fully expanded
const EXPANDED_STYLE = {
  top: '1.5rem',
  left: '1.5rem',
  height: 'calc(100vh - 3rem)',
  width: 'calc(100vw - 3rem)',
};

// Component provided to the user via a render function that should wrap the
// expandable content
const ExpandableComponent = forwardRef(
  (
    {
      isModal,
      initialClassName,
      style,
      eventHandlers,
      placeholder,
      toggle,
      // User provided props
      className,
      children,
      title,
      omitHeader,
      ...props
    },
    ref,
  ) => {
    if (!ref) {
      console.error(
        'ExpandableModal Error: Make sure you pass `expandableProps` into the `Expandable` Component in the render function.',
      );
      return null;
    }

    return (
      <>
        <dialog
          {...props}
          {...eventHandlers}
          className={classNames(initialClassName, className)}
          style={isModal ? style : {}}
          ref={ref}
        >
          {isModal &&
            !omitHeader && (
              <header>
                <span className="h3 m-0">{title}</span>
                <button
                  type="button"
                  className="btn btn-close"
                  onClick={toggle}
                  title="Close"
                  aria-label="Close"
                />
              </header>
            )}
          <section>{children}</section>
        </dialog>
        {isModal && placeholder}
      </>
    );
  },
);

export const ExpandableModal = ({ children: renderFn }) => {
  // Ref for the container element so we can get the position information and
  // render a placeholder in its place when we popout the content into a dialog
  const ref = useRef();
  // Flag to determine whether the dialog element should be a modal, or inline
  const [isModal, setIsModal] = useState(false);
  // State of the expandable modal
  const [openState, setOpenState] = useState(CLOSED);
  const isOpen = openState === OPEN,
    isOpening = openState === OPENING,
    isClosed = openState === CLOSED,
    isClosing = openState === CLOSING;
  // Styles for the placeholder element
  const [placeholderStyle, setPlaceholderStyle] = useState({});
  // Styles for the dialog to use before animating to full screen
  const [initialStyle, setInitialStyle] = useState({});
  // Styles for the dialog to use when fully expanded
  const [endingStyle, setEndingStyle] = useState(EXPANDED_STYLE);

  // When the isModal flag changes to true, remove the open attribute and call
  // the showModal function to show the dialog element
  useEffect(
    () => {
      if (isModal) {
        setOpenState(OPENING);
        ref.current.removeAttribute('open');
        ref.current.showModal();
      }
    },
    [isModal],
  );

  // Update the expandable modal when its state is changed
  useEffect(
    () => {
      if (isClosed) {
        // When the modal state is set to CLOSED, call the close function to
        // hide the dialog
        ref.current && ref.current.close && ref.current.close();
        // Reset the ending style to the default
        setEndingStyle(EXPANDED_STYLE);
        // Reset isModal to false
        setIsModal(false);
        ref.current.setAttribute('open', '');
      } else if (isOpening) {
        // When the modal state is set to OPENING, animate the dialog to full
        // screen, and on finish of the animation, set the state to OPEN. If on
        // finish, the state is CLOSING, then don't set to OPEN because the
        // user triggered the close before it was fully open
        const animation = ref.current.animate([initialStyle, endingStyle], {
          duration: 300,
          easing: 'ease-out',
        });
        animation.onfinish = () =>
          setOpenState(state => (state !== CLOSING ? OPEN : state));
      } else if (isClosing) {
        // When the modal state is set to CLOSING, animate the dialog back to
        // its original dimensions, and on finish of the animation, set the
        // state to CLOSED.
        const animation = ref.current.animate([endingStyle, initialStyle], {
          duration: 300,
          easing: 'ease-out',
        });
        animation.onfinish = () => setOpenState(CLOSED);
      }
    },
    [isClosed, isClosing, isOpening],
  );

  // Handler for toggling the modal
  const toggle = useCallback(
    () => {
      // If the modal is closed, expand it
      if (isClosed) {
        // Get the dimensions and position of the element
        const rect = ref.current.getBoundingClientRect();
        // Set the placeholder styles to have the same height so the page
        // doesn't scroll in the background
        setPlaceholderStyle({ height: `${rect.height}px` });
        // Set the initial styles for the dialog so it appears in the same
        // exact position as when it was inline
        setInitialStyle({
          top: `${rect.top}px`,
          left: `${rect.left}px`,
          height: `${rect.height}px`,
          width: `${rect.width}px`,
        });
        // Set isModal to true to trigger opening the modal
        setIsModal(true);
      }
      // If the modal is open or opening, close it
      else if (isOpen || isOpening) {
        // If it's opening, update the ending style to its current positions so
        // that the closing animation starts at that point, and not from fully
        // expanded
        if (isOpening) {
          const rect = ref.current.getBoundingClientRect();
          setEndingStyle({
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            height: `${rect.height}px`,
            width: `${rect.width}px`,
          });
        }
        // Set the state to CLOSING
        setOpenState(CLOSING);
      }
    },
    [isOpen, isOpening, isClosed],
  );

  // Event handler for cancel and keyup events to prevent propagation of Esc
  // key outside of the dialog
  const onCancel = e => {
    if (e.type === 'cancel' || e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'cancel') toggle();
    }
  };

  // Call the render function
  return renderFn({
    // Provide the wrapper component to render around the content that should
    // be expandable
    Expandable: ExpandableComponent,
    // Provide the props that need to be passed to the above wrapper component
    expandableProps: {
      isModal,
      initialClassName: classNames('expandable-modal', openState),
      style: isOpen || isClosing ? endingStyle : initialStyle,
      eventHandlers: {
        onCancel: !isClosed ? onCancel : undefined,
        onKeyUp: !isClosed ? onCancel : undefined,
      },
      toggle,
      placeholder: <div style={placeholderStyle} />,
      ref,
    },
    // Provide the toggle function so the user can trigger it as needed
    toggle,
    // Provide an isOpen state so the user can use it to determine when the
    // content is rendered in a dialog instead of inline in case they need to
    // change the dom or styles based on that
    isOpen: !isClosed,
  });
};
